import { getYear, getMonth, getDate, startOfMonth, lastDayOfMonth, formatISO, eachMonthOfInterval } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz'
import enGB from 'date-fns/locale/en-GB'

/**
 * @function parseDate - Returns the parsed date in the desired format '<month-name> <day> <year>, <HH:mm> <time-zone>'
 * @param {String} date - The date to be parsed in an ISO string format
 * @param {String} formatPattern - The date format pattern to be applied. Default: MMMM dd yyyy, HH:mm zzz
 * @returns {String} - The parsed date in the format MMMM dd yyyy, HH:mm zzz
 */
const parseDate = (dateString, formatPattern = 'MMMM dd yyyy, HH:mm zzz') => {
    const date = new Date(dateString)
    const timeZone = 'Europe/Amsterdam'
    return formatInTimeZone(date, timeZone, formatPattern, { locale: enGB })
}

/**
 * @function lastDateOfMonth - Returns the last day of the month and the time 23:59:59
 * @param {String} date - The date of which to find the last day in ISO string format
 * @returns {Date} - The last date of the given month
 */
const lastDateOfMonth = (date) => {
    const parsedDate = new Date(date)
    return new Date(getYear(parsedDate), getMonth(parsedDate), getDate(lastDayOfMonth(parsedDate)), 23, 59, 59)
}

/**
 * @function getOneMonthRange - Returns the first and the last day of the month in an ISO string format given the date passed
 * @param {String} date - The date in ISO string format
 * @returns {{endDate: String, startDate: String}} - The object containing the start and end dates in ISO format
 */
const getOneMonthRange = (date) => {
    const startDate = formatISO(startOfMonth(new Date(date)))
    const endDate = formatISO(lastDateOfMonth(date))
    return { startDate, endDate }
}

/**
 * @function getAvailableMonthsInAYear - Returns an array of months from January until the current available date
 * @param {Number} year - The year to check against
 * @returns {Object[]} - An array containing the start and end date for each month in the given year
 */
const getAvailableMonthsInAYear = (year) => {
    const currentDate = new Date()
    const endDate = year === currentDate.getFullYear()
        ? lastDateOfMonth(currentDate)
        : new Date(`December 31, ${year}`, 23, 59, 59)
    const months = eachMonthOfInterval({
        start: new Date(`January 1, ${year}`),
        end: endDate
    })
    return months.map(el => getOneMonthRange(formatISO(el)))
}

/**
 * @function getYearsRange - fetches the previous four years starting from the current one
 * @param {Number} n - go back n years (n=4 currentYear=2024, results=[2024,2023,2022,2021])
 * @returns {String[]} - An array containing the previous four years in descending order
 */

const getYearsRange = (n) => {
    const currentYear = new Date().getFullYear()
    const years = [`${currentYear}`]
    for (let i = 1; i < n; i++) {
        years.push(`${currentYear - i}`)
    }
    return years
}

const getLastNDaysRange = (n) => {
    const endDate = new Date()
    const startDate = new Date(new Date().setDate(new Date().getDate() - n + 1))
    // n amount of days - including end date
    return {
        startDate: formatISO(startDate),
        endDate: formatISO(endDate)
    }
}

const getMonthsSorted = (isReverse) => {
    const monthsOrder = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ]
    return isReverse === true ? monthsOrder.reverse() : monthsOrder
}

export { parseDate, getOneMonthRange, getAvailableMonthsInAYear, getYearsRange, getMonthsSorted, getLastNDaysRange }
